<template>
	<div style="padding-bottom: 100px">
		<div class="container">
			<div class="py-3 heading">
				<span>{{ t("loginRegister.new_password_forgot_password") }}</span>
			</div>

			<ContainerBox class="p-3 p-lg-4 position-relative">
				<Banner v-if="isSuccess" :background="'var(--green-100)'">
					<template #text>
						<span class="text-primary-green">{{
							t("loginRegister.new_password_succes")
						}}</span>
					</template>
				</Banner>

				<form v-else>
					<div class="my-3 title-4">
						<span>{{ t("loginRegister.new_password_create_new_password") }}</span>
					</div>

					<div id="email" class="mb-3">
						<TextInput
							:disabled="true"
							:value="email"
							:placeholder="t('loginRegister.contacts_email') + '*'"
							:container-box-border="
								state.showErrors && invalidFields.has('email')
									? '1px solid var(--primary-orange)'
									: undefined
							"
						/>
					</div>

					<div id="password1" class="mb-3">
						<TextInput
							:value="password1"
							:placeholder="t('loginRegister.new_password1') + '*'"
							:input-type="state.isSeePassword ? 'text' : 'password'"
							:container-box-border="
								state.showErrors && invalidFields.has('password1')
									? '1px solid var(--primary-orange)'
									: undefined
							"
							@input="(newVal) => handlePassword1Change(newVal)"
						>
							<template #after>
								<div
									v-if="password1"
									role="button"
									@click="() => handleToggleSeePassword()"
									class="mx-3 my-2"
								>
									<EyeSvg
										:opacity="state.isSeePassword ? 1 : 0.5"
										:color="'var(--primary-blue)'"
									/>
								</div>
							</template>
						</TextInput>
						<FieldError
							v-if="state.showErrors && invalidFields.has('password1')"
						>
							<template v-if="!password1">
								{{ t("loginRegister.user_info_mandatory_field") }}
							</template>
						</FieldError>
					</div>

					<div id="password2" class="mb-3">
						<TextInput
							:value="password2"
							:placeholder="t('loginRegister.new_password2') + '*'"
							:input-type="state.isSeePassword ? 'text' : 'password'"
							:container-box-border="
								state.showErrors && invalidFields.has('password2')
									? '1px solid var(--primary-orange)'
									: undefined
							"
							@input="(newVal) => handlePassword2Change(newVal)"
						>
							<template #after>
								<div
									v-if="password2"
									role="button"
									@click="() => handleToggleSeePassword()"
									class="mx-3 my-2"
								>
									<EyeSvg
										:opacity="state.isSeePassword ? 1 : 0.5"
										:color="'var(--primary-blue)'"
									/>
								</div>
							</template>
						</TextInput>
						<FieldError
							v-if="state.showErrors && invalidFields.has('password2')"
						>
							<template v-if="!password2">
								{{ t("loginRegister.user_info_mandatory_field") }}
							</template>
							<template v-else-if="password1 !== password2">
								{{ t("loginRegister.contacts_passwords_dont_match") }}
							</template>
						</FieldError>
					</div>

					<div class="my-4 d-flex justify-content-end">
						<div>
							<Button
								:disabled="state.showErrors && invalidFields.size > 0"
								@buttonClick="handleButtonClick"
							>
								<template #text>
									<span>{{ t("loginRegister.validate") }}</span>
								</template>
							</Button>
						</div>
					</div>
				</form>
				<Overlay v-if="loadingSet.size" :additionalClasses="['loader']" />
			</ContainerBox>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { reactive, computed, ref, onMounted, onBeforeUnmount } from "vue"

// common components
import Banner from "@/components/LoginRegister/common/Banner/v1/Banner.vue"
import ContainerBox from "@/components/LoginRegister/common/ContainerBox/v1/ContainerBox.vue"
import TextInput from "@/components/LoginRegister/common/inputs/Text/v1/Text.vue"
import FieldError from "@/components/LoginRegister/common/FieldError/v1/FieldError.vue"
import Button from "@/components/LoginRegister/common/Button/v1/Button.vue"
import Overlay from "@/components/LoginRegister/common/Overlay/v1/Overlay.vue"

// svg
import EyeSvg from "@/components/LoginRegister/svg/EyeSvg.vue"

// composables
import useLoading, { isLoadingAny } from "@/composables/useLoading"

// libs
import { onKeyStroke } from "@vueuse/core"

// logic
import newPassword from "@/logic/contact/newPassword"
import decodeHash from "@/logic/contact/decodeHash"
import checkIsHashExpired from "@/logic/contact/checkIsHashExpired"

// utils
import {getQueryParams} from "@/utils"

// state
import email from "@/state/email"
import current_form from "@/state/current_form"
import showResetPopUp from "@/state/showResetPopUp"
import loadingSet from "@/state/loadingSet"

// config
import UserInfoFormEnum from "@/config/UserInfoFormEnum"

// translations
import { useI18n } from "vue-i18n"
const {t} = useI18n()

// state
const state = reactive({
	isSeePassword: false,
	showErrors: false,
})

const isSuccess = ref(false)
const hash = ref("")
const password1 = ref("")
const password2 = ref("")

// computed
const invalidFields = computed(() => {
	const invalidFields = new Set<string>()

	if (!password1.value) invalidFields.add("password1")
	if (!password2.value) invalidFields.add("password2")
	if (password1.value.length < 8) invalidFields.add("password1")
	if (password2.value.length < 8) invalidFields.add("password2")

	if (password1.value !== password2.value) invalidFields.add("password2")
	return invalidFields
})

// handlers
const handleButtonClick = async () => {
	if (invalidFields.value.size > 0) {
		state.showErrors = true
		return
	}
	state.showErrors = false

	const { startLoadingThis, stopLoadingThis } = useLoading("new-password-call")
	startLoadingThis()
	await newPassword(hash.value, password1.value)
	stopLoadingThis()
	current_form.value = UserInfoFormEnum.Login
	isSuccess.value = true
}
const handlePassword1Change = (newValue: string) => {
	password1.value = newValue
}
const handlePassword2Change = (newValue: string) => {
	password2.value = newValue
}
const handleToggleSeePassword = () => {
	state.isSeePassword = !state.isSeePassword
}

onMounted(async () => {
	const { startLoadingThis, stopLoadingThis } = useLoading(
		"new-password-mounted"
	)
	startLoadingThis()

	document.querySelector("body")!.style.visibility = "visible"
	const query = getQueryParams()
	if (!query.hash) throw new Error("hash must be a query param")
	const decodedHash = decodeHash(query.hash)
	if (!decodedHash) throw new Error("no decoded hash in newPassword")
	if (!decodedHash.email) throw new Error("email must be in decoded hash")
	if (!decodedHash.exp) throw new Error("exp must be in decoded hash")

	hash.value = query.hash
	email.value = decodedHash.email

	const expirationMiliSeconds: number = decodedHash.exp * 1000
	const isDecodedHashExpired: boolean = checkIsHashExpired(
		expirationMiliSeconds
	)
	if (isDecodedHashExpired) {
		current_form.value = UserInfoFormEnum.Reset
		// If the hash is expired, set the state to true to display the popup to reset again
		showResetPopUp.value = true
	}

	// onKeyStroke
	const stop = onKeyStroke("Enter", () => {
		handleButtonClick()
	})
	onBeforeUnmount(() => stop())

	stopLoadingThis()
})
</script>

<style  scoped></style>
